import { ProductPlatformGroup } from './product-offering-feature.model';
import { ProductCode } from './product.model';
import { SlotCode } from './slot.model';

export interface ProductDescription {
  name: string;
  longname: string;
  shortname?: string;
  description: string;
}

export const PLATFORM_TO_PRODUCT_PLATFORM_GROUP: Record<string, ProductPlatformGroup> = {
  JOBS: ProductPlatformGroup.Jobs,
  JS24: ProductPlatformGroup.JobScout24,
  JOBUP: ProductPlatformGroup.JobUp,
};

export const PRODUCT_PLATFORM_GROUP_TRANSLATIONS: {
  [key in ProductPlatformGroup]: string;
} = {
  [ProductPlatformGroup.Jobs]: 'PRODUCT_OFFERING.PLATFORM.JOBS',
  [ProductPlatformGroup.JobScout24]: 'PRODUCT_OFFERING.PLATFORM.JOB-SCOUT-24',
  [ProductPlatformGroup.JobUp]: 'PRODUCT_OFFERING.PLATFORM.JOBUP',
};

/**
 * Configuration for products with special cases (non-standard translation keys or missing descriptions).
 * Most products follow the standard pattern and don't need entries here.
 */
const PRODUCT_TRANSLATIONS_CONFIG: Partial<
  Record<ProductCode | SlotCode, Partial<ProductDescription> & { keyOverride?: string }>
> = {
  [ProductCode.JOBSFLEX]: { description: '' },
  [ProductCode.JOBSOFFER22L]: { shortname: 'PRODUCT_OPTIONS.PRODUCT.SHORTNAME_JOBSOFFER22L' },
  [ProductCode.JOBSOFFER22XL]: { shortname: 'PRODUCT_OPTIONS.PRODUCT.SHORTNAME_JOBSOFFER22XL' },
};

/**
 * Factory function to generate translation keys based on product code.
 * Reduces repetition by automatically generating i18n keys following the pattern:
 * PRODUCT_OPTIONS.PRODUCT.NAME_<PRODUCT_CODE>
 *
 * The keys array is generated dynamically from the ProductCode and SlotCode enums,
 * ensuring it stays in sync when new products are added.
 *
 * Executed once at module initialization via IIFE pattern.
 */
export const PRODUCT_TRANSLATIONS = (() => {
  // Generate keys from enums dynamically
  const keys: (ProductCode | SlotCode)[] = [
    ...Object.values(ProductCode),
    ...Object.values(SlotCode),
  ];

  const result: Record<ProductCode | SlotCode, ProductDescription> = {} as any; // eslint-disable-line @typescript-eslint/no-explicit-any

  keys.forEach((code) => {
    const config = PRODUCT_TRANSLATIONS_CONFIG[code];
    const keyOverride = config?.keyOverride || code;
    const baseKey = `PRODUCT_OPTIONS.PRODUCT`;

    const translation: ProductDescription = {
      name: `${baseKey}.NAME_${keyOverride}`,
      longname: `${baseKey}.LONGNAME_${keyOverride}`,
      description: `${baseKey}.DESCRIPTION_${keyOverride}`,
      ...config,
    };

    // Remove undefined properties
    Object.keys(translation).forEach((key) => {
      if (translation[key as keyof ProductDescription] === undefined) {
        delete translation[key as keyof ProductDescription];
      }
    });

    result[code] = translation;
  });

  return result;
})();

export const BEST_VALUE_PRODUCTS: ProductCode[] = [
  ProductCode.JOBSOFFER22M,
  ProductCode.JS24ADVANCED,
  ProductCode.JOBUP24ADVANCED,
];

export const BEST_VALUE_PRODUCT_TRANSLATIONS: { [key in ProductPlatformGroup]: string } = {
  [ProductPlatformGroup.Jobs]: 'PRODUCT_OFFERING.JOBS.BEST_VALUE',
  [ProductPlatformGroup.JobScout24]: 'PRODUCT_OFFERING.JOBS24.BEST_VALUE',
  [ProductPlatformGroup.JobUp]: 'PRODUCT_OFFERING.JOBUP.BEST_VALUE',
};

export const LITE_PRODUCT_AVAILABLE_SUBCATEGORIES = [
  {
    name: 'PRODUCT_OFFERING.JOBS.LITE_PRODUCT.SUBCATEGORIES.TITLE.ADMIN_HR_CONSULTING_CEO',
    items: [
      'PRODUCT_OFFERING.JOBS.LITE_PRODUCT.SUBCATEGORIES.ITEM.SECRETARY_RECEPTION',
      'PRODUCT_OFFERING.JOBS.LITE_PRODUCT.SUBCATEGORIES.ITEM.MANAGEMENT_ASSISTANCE',
    ],
  },
  {
    name: 'PRODUCT_OFFERING.JOBS.LITE_PRODUCT.SUBCATEGORIES.TITLE.PURCHASING_LOGISTICS_TRADING',
    items: [
      'PRODUCT_OFFERING.JOBS.LITE_PRODUCT.SUBCATEGORIES.ITEM.PURCHASING_PROCUREMENT',
      'PRODUCT_OFFERING.JOBS.LITE_PRODUCT.SUBCATEGORIES.ITEM.LOGISTICS_SUPPLY_CHAIN',
    ],
  },
  {
    name: 'PRODUCT_OFFERING.JOBS.LITE_PRODUCT.SUBCATEGORIES.TITLE.MACHINE_PLANT_MANUFACTURING',
    items: [
      'PRODUCT_OFFERING.JOBS.LITE_PRODUCT.SUBCATEGORIES.ITEM.MANUFACTURING_PRODUCTION_INDUSTRY',
    ],
  },
  {
    name: 'PRODUCT_OFFERING.JOBS.LITE_PRODUCT.SUBCATEGORIES.TITLE.VEHICLES_CRAFT_WAREHOUSE_TRANSPORT',
    items: [
      'PRODUCT_OFFERING.JOBS.LITE_PRODUCT.SUBCATEGORIES.ITEM.FACILITY_MANAGEMENT_CLEANING',
      'PRODUCT_OFFERING.JOBS.LITE_PRODUCT.SUBCATEGORIES.ITEM.STORAGE',
      'PRODUCT_OFFERING.JOBS.LITE_PRODUCT.SUBCATEGORIES.ITEM.TRANSPORT_AIR_SEA_RAIL_ROAD',
    ],
  },
  {
    name: 'PRODUCT_OFFERING.JOBS.LITE_PRODUCT.SUBCATEGORIES.TITLE.CATERING_FOOD_TOURISM',
    items: [
      'PRODUCT_OFFERING.JOBS.LITE_PRODUCT.SUBCATEGORIES.ITEM.COOKING_CUISINE',
      'PRODUCT_OFFERING.JOBS.LITE_PRODUCT.SUBCATEGORIES.ITEM.SERVICE_BAR',
      'PRODUCT_OFFERING.JOBS.LITE_PRODUCT.SUBCATEGORIES.ITEM.FRONTDESK_PORTER',
      'PRODUCT_OFFERING.JOBS.LITE_PRODUCT.SUBCATEGORIES.ITEM.ROOMSERVICE_CLEANING_HOUSEKEEPING',
    ],
  },
  {
    name: 'SURVEILLANCE_POLICE_CUSTOMS_RESCUE',
    items: [
      'PRODUCT_OFFERING.JOBS.LITE_PRODUCT.SUBCATEGORIES.ITEM.SURVEILLANCE_CONTROL_SECURITY',
      'PRODUCT_OFFERING.JOBS.LITE_PRODUCT.SUBCATEGORIES.ITEM.RESCUE_FIRE',
    ],
  },
];

export const JOBUP_LITE_PRODUCT_AVAILABLE_ITEMS = [
  'PRODUCT_OFFERING.JOBUP.LITE_PRODUCT.ITEM.ADMINISTRATION',
  'PRODUCT_OFFERING.JOBUP.LITE_PRODUCT.ITEM.ASSISTANCE',
  'PRODUCT_OFFERING.JOBUP.LITE_PRODUCT.ITEM.KITCHEN',
  'PRODUCT_OFFERING.JOBUP.LITE_PRODUCT.ITEM.LOGISTICS',
  'PRODUCT_OFFERING.JOBUP.LITE_PRODUCT.ITEM.PUBLIC_ADMINISTRATION',
  'PRODUCT_OFFERING.JOBUP.LITE_PRODUCT.ITEM.PURCHASING',
  'PRODUCT_OFFERING.JOBUP.LITE_PRODUCT.ITEM.SALES',
  'PRODUCT_OFFERING.JOBUP.LITE_PRODUCT.ITEM.SECRETARIAL',
  'PRODUCT_OFFERING.JOBUP.LITE_PRODUCT.ITEM.ROOM_SERVICE',
  'PRODUCT_OFFERING.JOBUP.LITE_PRODUCT.ITEM.TRANSPORTATION',
  'PRODUCT_OFFERING.JOBUP.LITE_PRODUCT.ITEM.BAR',
  'PRODUCT_OFFERING.JOBUP.LITE_PRODUCT.ITEM.WAREHOUSEMAN',
  'PRODUCT_OFFERING.JOBUP.LITE_PRODUCT.ITEM.SECTION_MANAGER',
];

/**
 * Deprecated products that should not be displayed to users.
 * These are filtered out at the API level in ProductResource.
 *
 * Includes:
 * - Legacy Jobs products (JOBSLIMITED, JOBSSTARTER)
 * - Deprecated JS24 products (JS24SOLO, JS24PLUS)
 */
export const DEPRECATED_PRODUCT_CODES: readonly ProductCode[] = [
  ProductCode.JOBSLIMITED,
  ProductCode.JOBSSTARTER,
  ProductCode.JS24SOLO,
  ProductCode.JS24PLUS,
] as const;

export const FREE_PRODUCT_CODES: ProductCode[] = [
  ProductCode.JS24FREE,
  ProductCode.JOBSOFFER22FREE,
  ProductCode.JOBUP24FREE,
] as const;

/**
 * The order every platform picker lists its options in, before the recommended one
 * is lifted to the top. Shared so the offering page and the publication modal cannot
 * drift apart on it.
 */
export const PRODUCT_PLATFORM_GROUP_ORDER: readonly ProductPlatformGroup[] = [
  ProductPlatformGroup.JobUp,
  ProductPlatformGroup.Jobs,
  ProductPlatformGroup.JobScout24,
];

export const PRODUCT_PLATFORM_GROUP_DESCRIPTIONS: {
  [key in ProductPlatformGroup]: string;
} = {
  [ProductPlatformGroup.JobUp]: 'PRODUCT_OFFERING.JOBUP_DESCRIPTION',
  [ProductPlatformGroup.Jobs]: 'PRODUCT_OFFERING.JOBS_DESCRIPTION',
  [ProductPlatformGroup.JobScout24]: 'PRODUCT_OFFERING.JOBSCOUT24_DESCRIPTION',
};

export const CANTONS_PER_PRODUCT_PLATFORM_GROUP: {
  [key in ProductPlatformGroup]: string[];
} = {
  [ProductPlatformGroup.JobUp]: ['Geneva', 'Vaud', 'Neuchâtel', 'Jura', 'Fribourg', 'Valais'],
  [ProductPlatformGroup.Jobs]: [
    'Aargau',
    'Appenzell Ausserrhoden',
    'Appenzell Innerrhoden',
    'Basel-Stadt',
    'Basel-Landschaft',
    'Bern',
    'Glarus',
    'Graubünden',
    'Lucerne',
    'Nidwalden',
    'Obwalden',
    'Schaffhausen',
    'Schwyz',
    'Solothurn',
    'St. Gallen',
    'Ticino',
    'Thurgau',
    'Uri',
    'Zug',
    'Zurich',
  ],
  [ProductPlatformGroup.JobScout24]: [],
};
